import { FilterTable } from "@/features/FilterTable/FilterTable";
import { Field } from "@/features/FilterTable/types";
import { DepField, makeFiltersBuilder } from "@/features/FilterTable/utils";
import { preprocessTextAsyncAutocompleteField } from "@/features/FormCard/utils";
import {
  CLIENT_GROUP_OPTIONS,
  CLIENT_GROUP_VALUES,
} from "@/features/Workspaces/clientGroups";
import { z } from "zod";

// No Rails enums defined on Workbook, so all enum-like fields are handled as plain text or async lookups

export const filterWorkbookSchema = z.object({
  name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional(),
  menu_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional(),
  menu_quantilia_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional(),
  uuid_contains: z.string().nullable().optional(),
  client_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional(),
  workspace_group_contains: z.enum(CLIENT_GROUP_VALUES).optional().nullable(),
  group_user_name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional(),
});

export type FilterWorkbookSchema = z.infer<typeof filterWorkbookSchema>;

const workbookDepFields: readonly DepField<FilterWorkbookSchema>[] = [
  {
    name: "name_contains",
    key: "name",
    extract: "text",
    bucket: "equals",
  },
  {
    name: "menu_contains",
    key: "menu",
    extract: "text",
    bucket: "equals",
  },
  {
    name: "menu_quantilia_contains",
    key: "menu_quantilia",
    extract: "text",
    bucket: "equals",
  },
  {
    name: "client_contains",
    key: "client",
    extract: "text",
    bucket: "equals",
  },
  {
    name: "workspace_group_contains",
    key: "workspace_group",
    extract: "text",
    bucket: "equals",
  },
  {
    name: "group_user_name_contains",
    key: "user_group_id",
    extract: "id",
    bucket: "equals",
  },
];

export const fields: Field[] = [
  {
    name: "name_contains",
    label: "Name",
    type: "asyncAutocomplete",
    base_url: "tableau-quantilia/workbooks",
    column_name: "name",
    filters: makeFiltersBuilder<FilterWorkbookSchema>(
      workbookDepFields,
      "name_contains",
    ),
  },
  {
    name: "menu_contains",
    label: "Menu",
    type: "asyncAutocomplete",
    base_url: "tableau-quantilia/workbooks",
    column_name: "menu",
    filters: makeFiltersBuilder<FilterWorkbookSchema>(
      workbookDepFields,
      "menu_contains",
    ),
  },
  {
    name: "menu_quantilia_contains",
    label: "Menu Quantilia",
    type: "asyncAutocomplete",
    base_url: "tableau-quantilia/workbooks",
    column_name: "menu_quantilia",
    filters: makeFiltersBuilder<FilterWorkbookSchema>(
      workbookDepFields,
      "menu_quantilia_contains",
    ),
  },
  {
    name: "uuid_contains",
    label: "UUID",
    type: "input",
  },
  {
    name: "workspace_group_contains",
    label: "workbooks.columns.workspace_group",
    type: "select",
    useTranslation: false,
    options: CLIENT_GROUP_OPTIONS,
  },
  {
    name: "client_contains",
    label: "Client",
    type: "asyncAutocomplete",
    base_url: "tableau-quantilia/workbooks",
    column_name: "client",
    filters: makeFiltersBuilder<FilterWorkbookSchema>(
      workbookDepFields,
      "client_contains",
    ),
  },
  {
    name: "group_user_name_contains",
    label: "User Group",
    type: "asyncAutocomplete",
    base_url: "tableau-quantilia/workbooks",
    column_name: "group_user_name",
  },
];

export const client = "qtl - tableau";

export function FilterWorkbook() {
  return (
    <FilterTable
      client={client}
      schema={filterWorkbookSchema}
      tableName="workbooks"
      //customBuildPath={() => `${client}/workbooks`}
      fields={fields}
    />
  );
}
